import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  UsersIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  TrashIcon,
  FingerPrintIcon,
  CpuChipIcon,
  GlobeAmericasIcon,
  HeartIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/solid';
import { useAuth } from '../connections/AuthContext';
import routes_web from '../connections/routes_web';
import { useClickOutside } from 'services/useClickOutside';
import syncService from '../offline_items/syncService';

// ##### Wipe confirmation modal (rendered outside the sidebar so it survives the sidebar closing)
// pendingCount = unsynced changes in the sync queue; 0 → plain confirmation.
const WipeConfirmModal = ({ pendingCount, isBusy, onCancel, onWipe, onSyncAndWipe }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] px-4">
    <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md">
      <h3 className="text-lg font-semibold mb-4">Logout and wipe this device</h3>
      <p className="mb-2 text-sm text-gray-700">
        Deletes all app data stored in this browser (offline data, cache, settings) and logs you out.
      </p>
      {pendingCount > 0 && (
        <p className="mb-2 text-sm text-red-600 font-medium">
          {pendingCount} change{pendingCount > 1 ? 's are' : ' is'} not synced yet and will be lost if you wipe now.
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-3 mt-6">
        <button
          onClick={onCancel}
          disabled={isBusy}
          className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
        >
          Cancel
        </button>
        {pendingCount > 0 && navigator.onLine && (
          <button
            onClick={onSyncAndWipe}
            disabled={isBusy}
            className="px-4 py-2 rounded-xl bg-gray-800 hover:bg-gray-900 text-white shadow"
          >
            Sync and wipe
          </button>
        )}
        <button
          onClick={onWipe}
          disabled={isBusy}
          className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white shadow"
        >
          {isBusy ? 'Wiping...' : pendingCount > 0 ? 'Wipe anyway' : 'Wipe'}
        </button>
      </div>
    </div>
  </div>
);

// ##### HELPER: Count unsynced changes (pending, retrying, failed)
const countUnsynced = async () => {
  try {
    const items = await syncService.getAllQueueItems();
    return items.length;
  } catch (error) {
    console.error('Failed to read sync queue:', error);
    return 0;
  }
};

// Icon and route map
const MODULES_CONFIG = {
  core: { icon: CpuChipIcon, title: 'Core', path: routes_web.core_main },
  health: { icon: HeartIcon, title: 'Health', path: routes_web.health_main },
  travel: { icon: GlobeAmericasIcon, title: 'Travel', path: routes_web.travel_main },
  planner: { icon: CalendarDaysIcon, title: 'Planner', path: routes_web.planner_main },
  finance: { icon: BanknotesIcon, title: 'Finance', path: routes_web.finance_main },
  notepad: { icon: DocumentTextIcon, title: 'Notepad', path: routes_web.notepad_main },
  identity: { icon: FingerPrintIcon, title: 'Identity', path: routes_web.identity_main },
};

const SidebarMenu = ({ isOpen, onClose }) => {
  const { user, logout, logoutAndWipe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);
  const sidebarRef = useRef(null);
  const [wipePendingCount, setWipePendingCount] = useState(null); // null = modal closed
  const [isWiping, setIsWiping] = useState(false);

  // Handle click outside to close sidebar
  useClickOutside([sidebarRef], onClose, isOpen);

  useEffect(() => {
    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isOpen) {
      setShouldRender(true);
      // Use requestAnimationFrame to ensure DOM is ready before animating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      // Trigger slide-out animation
      setIsVisible(false);
      // Remove from DOM after animation completes (300ms)
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        timeoutRef.current = null;
      }, 300);
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isOpen]);

  const openAccountView = (path) => {
    onClose();
    navigate(path, { state: { from: location.pathname } });
  };

  const handleLogout = () => {
    onClose();
    logout();
  };

  const handleWipeClick = async () => {
    onClose();
    setWipePendingCount(await countUnsynced());
  };

  const handleWipe = async () => {
    setIsWiping(true);
    await logoutAndWipe(); // Ends with a full page load
  };

  const handleSyncAndWipe = async () => {
    setIsWiping(true);
    await syncService.sync();
    const remaining = await countUnsynced();
    if (remaining > 0) {
      // Something failed or conflicted: let the user decide again
      setIsWiping(false);
      setWipePendingCount(remaining);
      return;
    }
    await logoutAndWipe();
  };

  const wipeModal = wipePendingCount !== null && (
    <WipeConfirmModal
      pendingCount={wipePendingCount}
      isBusy={isWiping}
      onCancel={() => setWipePendingCount(null)}
      onWipe={handleWipe}
      onSyncAndWipe={handleSyncAndWipe}
    />
  );

  const handleModuleClick = (path) => {
    onClose();
    navigate(path);
  };

  if (!shouldRender) return wipeModal || null;

  const preferences = user?.app_preferences || [];
  const visibleModules = preferences
    .filter(pref => pref.app_visible && pref.app_enabled)
    .sort((a, b) => a.app_order - b.app_order);

  return (
    <>
    {wipeModal}
    <div
      ref={sidebarRef}
      className={`fixed top-0 right-0 h-full w-[60%] sm:w-[280px] bg-gray-900 bg-opacity-95 backdrop-blur-sm shadow-xl z-[60] transform transition-transform duration-300 ease-in-out ${
        isVisible ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      {/* Content */}
      <div className="p-4">
        {/* Close Button - Top Right */}
        <div className="flex justify-end mb-4">
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-800 transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* User Block */}
        <div className="mb-4">
          <p className="text-sm text-gray-400 mb-1">User</p>
          <p className="text-lg font-bold text-white mb-3">{user?.username || 'Guest'}</p>

          {/* User Actions */}
          <div className="space-y-1">
            <button
              onClick={() => openAccountView(routes_web.accounts_usersettings)}
              className="w-full flex items-center px-4 py-2 text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <UsersIcon className="h-5 w-5 mr-3 text-gray-300" />
              <span className="font-medium">User Profile</span>
            </button>

            <button
              onClick={() => openAccountView(routes_web.accounts_generalsettings)}
              className="w-full flex items-center px-4 py-2 text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Cog6ToothIcon className="h-5 w-5 mr-3 text-gray-300" />
              <span className="font-medium">General Settings</span>
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-gray-300" />
              <span className="font-medium">Logout</span>
            </button>

            <button
              onClick={handleWipeClick}
              className="w-full flex items-center px-4 py-2 text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <TrashIcon className="h-5 w-5 mr-3 text-red-400" />
              <span className="font-medium">Logout and wipe device</span>
            </button>
          </div>
        </div>

        {/* Divider */}
        <hr className="border-t border-gray-700 my-4" />

        {/* Quick Navigation - Modules */}
        <div>
          <p className="text-sm text-gray-400 mb-3">Modules</p>
          <div className="space-y-1">
            {visibleModules.length === 0 ? (
              <p className="text-sm text-gray-500 px-4">No modules available</p>
            ) : (
              visibleModules.map(pref => {
                const config = MODULES_CONFIG[pref.app_key];
                if (!config) return null;
                const Icon = config.icon;
                return (
                  <button
                    key={pref.app_key}
                    onClick={() => handleModuleClick(config.path)}
                    className="w-full flex items-center px-4 py-2 text-white hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <Icon className="h-5 w-5 mr-3 text-gray-300" />
                    <span className="font-medium">{config.title}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SidebarMenu;
